import { Box, IconButton } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { FC, useState } from "react";
import { MoreHorizontal } from "react-feather";
import { Transaction } from "../../../models";
import { TransactionType } from "../../../models/transactions/transactionType";
import { formatCurrency, formatDate } from "../../../utils/formatters";
import { toTitleCase } from "../../../utils/toTitleCase";
import { LoadingSpinner } from "../../shared/LoadingSpinner";
import { Table } from "../../shared/Table";
import { PurchaseTransactionDrawer } from "./SideDrawers/PurchaseTransactionDrawer";
import { TopUpTransactionDrawer } from "./SideDrawers/TopUpTransactionDrawer";
import { AdjustmentTransactionDrawer } from "./SideDrawers/AdjustmentTransactionDrawer";
import { RefundTransactionDrawer } from "./SideDrawers/RefundTransactionDrawer";
import { BalanceExpireTransactionDrawer } from "./SideDrawers/BalanceExpireTransactionDrawer";
import { AggregationTransactionDrawer } from "./SideDrawers/AggregationTransactionDrawer";
import { ReimbursementTransactionDrawer } from "./SideDrawers/ReimbursementTransactionDrawer";
import { useData } from "../../../utils/useData";
import { Tag } from "../../../models/tag";

const formatAmount = (amount: number) => {
  // If amount is negative, show it as positive with a plus sign
  // If amount is positive, show it as is
  if (amount < 0) {
    return `+${formatCurrency(-amount)}`;
  }
  return formatCurrency(amount);
};

const TransactionsTable: FC<{
  transactions: Transaction[];
  totalItems: number;
  page: number;
  pageSize: number;
  updatePage: (value: number) => void;
  updatePageSize: (value: number) => void;
  isLoading: boolean;
  employerId?: string;
  employees?: { id: string; firstName: string; lastName: string; tags?: Tag[] }[];
  accounts?: { id: string; name: string }[];
  serviceSectors?: { id: string; name: string }[];
}> = ({
  transactions,
  totalItems,
  page,
  pageSize,
  updatePage,
  updatePageSize,
  isLoading,
  employerId,
  employees,
  accounts,
  serviceSectors
}) => {
  const { plans } = useData();
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const renderTransactionMetadata = (transaction: Transaction) => {
    return (
      <>
        <IconButton
          onClick={() => {
            setSelectedTransaction(transaction);
            setIsDrawerOpen(true);
          }}
        >
          <MoreHorizontal />
        </IconButton>
      </>
    );
  };

  const columns: ColumnDef<Transaction>[] = [
    {
      header: "Date",
      meta: {
        filterType: "dateRange"
      },
      enableColumnFilter: false,
      cell: ({ row }) => {
        const formattedDate = formatDate(row.original.createdAt);
        return <Box>{formattedDate}</Box>;
      }
    },
    {
      cell: ({ row }) => (
        <Box>{formatAmount(row.original.billingAmount.amount)}</Box>
      ),
      header: "Amount",
      enableColumnFilter: false
    },
    {
      header: "User",
      cell: ({ row }) => (
        <Box>{row.original.customerFullName}</Box>
      ),
      enableColumnFilter: false
    },
    {
      header: "Status",
      cell: ({ row }: { row: { original: Transaction } }) => {
        return <Box>{toTitleCase(row.original.status)}</Box>;
      },
      enableColumnFilter: false
    },
    {
      header: "Info",
      cell: ({ row }: { row: { original: Transaction } }) => {
        return renderTransactionMetadata(row.original);
      }
    }
  ];

  if (isLoading || plans.isLoading) return <LoadingSpinner />;

  return (
    <>
      <Table<Transaction>
        data={transactions}
        totalItems={totalItems}
        columns={columns}
        isServerSidePagination={true}
        paginationPage={page}
        paginationPageSize={pageSize}
        onPageChange={updatePage}
        onPageSizeChange={updatePageSize}
        displaySearch={true}
        displayFilters={true}
      />

      {/* Render the appropriate drawer based on transaction type */}
      {selectedTransaction?.type === TransactionType.PURCHASE && (
        <PurchaseTransactionDrawer
          transaction={selectedTransaction}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          employerId={employerId}
          employees={employees}
          accounts={accounts}
          serviceSectors={serviceSectors}
          plans={plans.data?.items}
        />
      )}
      {selectedTransaction?.type === TransactionType.TOP_UP && (
        <TopUpTransactionDrawer
          transaction={selectedTransaction}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          employerId={employerId}
          employees={employees}
          accounts={accounts}
          serviceSectors={serviceSectors}
          plans={plans.data?.items}
        />
      )}
      {selectedTransaction?.type === TransactionType.ADJUSTMENT && (
        <AdjustmentTransactionDrawer
          transaction={selectedTransaction}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          employerId={employerId}
          employees={employees}
          accounts={accounts}
          serviceSectors={serviceSectors}
          plans={plans.data?.items}
        />
      )}
      {selectedTransaction?.type === TransactionType.REFUND && (
        <RefundTransactionDrawer
          transaction={selectedTransaction}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          employerId={employerId}
          employees={employees}
          accounts={accounts}
          serviceSectors={serviceSectors}
          plans={plans.data?.items}
        />
      )}
      {selectedTransaction?.type === TransactionType.BALANCE_EXPIRE && (
        <BalanceExpireTransactionDrawer
          transaction={selectedTransaction}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          employerId={employerId}
          employees={employees}
          accounts={accounts}
          serviceSectors={serviceSectors}
          plans={plans.data?.items}
        />
      )}
      {selectedTransaction?.type === TransactionType.AGGREGATION && (
        <AggregationTransactionDrawer
          transaction={selectedTransaction}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          employerId={employerId}
          employees={employees}
          accounts={accounts}
          serviceSectors={serviceSectors}
          plans={plans.data?.items}
        />
      )}
      {selectedTransaction?.type === TransactionType.REIMBURSEMENT && (
        <ReimbursementTransactionDrawer
          transaction={selectedTransaction}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          employerId={employerId}
          employees={employees}
          accounts={accounts}
          serviceSectors={serviceSectors}
          plans={plans.data?.items}
        />
      )}
    </>
  );
};

export { TransactionsTable }; 
